import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SectionHeadingProps {
  /** Heading text; may carry an inline count. */
  children: ReactNode
  icon?: LucideIcon
  description?: ReactNode
  /**
   * `'page'` (default): the h2 of a page whose sections are its tables.
   * `'section'`: an h3 at the size of the card titles around it, for a table
   * inside a page that already has section-level cards.
   */
  size?: 'page' | 'section'
}

/**
 * Heading of a page section that holds a DataTable. The table draws its own
 * card frame, so the section is not wrapped in a `Card`; this keeps the
 * `CardTitle` / `CardDescription` typography such a Card header used to give.
 */
export function SectionHeading({ children, icon: Icon, description, size = 'page' }: SectionHeadingProps) {
  const Heading = size === 'page' ? 'h2' : 'h3'
  return (
    <div className={size === 'page' ? 'space-y-1.5' : 'space-y-1'}>
      <Heading
        className={cn(
          'flex items-center gap-2',
          size === 'page' ? 'text-2xl font-semibold leading-none tracking-tight' : 'text-lg font-medium',
        )}
      >
        {Icon && <Icon className="h-5 w-5" aria-hidden="true" />}
        {children}
      </Heading>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
    </div>
  )
}
